import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const ProjectsView = () => {
  const { projects, users, createProject, updateProject } = useData();
  const { currentUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'planning',
    startDate: '',
    endDate: '',
    managerId: '',
    budget: '',
    teamMembers: []
  });

  const projectManagers = users.filter(u => u.role === 'project_manager');
  const employees = users.filter(u => u.role === 'employee');

  // Filter projects based on user role
  const filteredProjects = useMemo(() => {
    if (!currentUser) return [];

    if (currentUser.role === 'owner') {
      // Owner can see all projects
      return projects;
    } else if (currentUser.role === 'project_manager') {
      // Project manager can only see projects they are assigned to manage
      return projects.filter(project => project.managerId === currentUser.id);
    } else {
      // Other roles (employees) can see projects they are team members of
      return projects.filter(project => 
        project.teamMembers && project.teamMembers.includes(currentUser.id)
      );
    }
  }, [projects, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const managerData = users.find(u => u.id === formData.managerId);
    const projectData = {
      ...formData,
      managerName: managerData?.name || '',
      progress: editingProject ? editingProject.progress : 0,
      budget: parseFloat(formData.budget) || 0
    };

    if (editingProject) {
      await updateProject(editingProject.id, projectData);
      setEditingProject(null);
    } else {
      await createProject(projectData);
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'planning',
      startDate: '',
      endDate: '',
      managerId: '',
      budget: '',
      teamMembers: []
    });
    setShowCreateForm(false);
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setFormData({
      name: project.name,
      description: project.description,
      status: project.status,
      startDate: project.startDate,
      endDate: project.endDate,
      managerId: project.managerId,
      budget: project.budget.toString(),
      teamMembers: project.teamMembers || []
    });
    setEditingProject(project);
    setShowCreateForm(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'status-planning',
      active: 'status-active',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return colors[status] || 'status-on-hold';
  };

  const canCreateProject = currentUser?.role === 'owner' || currentUser?.role === 'project_manager';
  const canEditProject = currentUser?.role === 'owner' || currentUser?.role === 'project_manager';

  return (
    <div className="projects-view">
      <div className="page-header">
        <h2 className="page-title">Projects Management</h2>
        {canCreateProject && (
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i>
            New Project
          </button>
        )}
      </div>

      
{showCreateForm && (
  <div className="modal-overlay">
    <div className="modal">
      <div className="modal-header">
        <h3>{editingProject ? 'Edit Project' : 'Create New Project'}</h3>
        <button onClick={resetForm} className="modal-close">
          <i className="fas fa-times"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="modal-body">
        <div className="form-group">
          <label className="form-label">Project Name</label>
          <input
            type="text"
            className="form-control"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Enter project name"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={3}
            placeholder="Describe the project goals and requirements"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-control"
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <option value="planning">📋 Planning</option>
              <option value="active">🚀 Active</option>
              <option value="completed">✅ Completed</option>
              <option value="on_hold">⏸️ On Hold</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Budget</label>
            <input
              type="number"
              className="form-control"
              value={formData.budget}
              onChange={(e) => setFormData({...formData, budget: e.target.value})}
              placeholder="Enter project budget"
              min="0"
              step="100"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.endDate}
              onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              min={formData.startDate || new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Project Manager</label>
          <select
            className="form-control"
            value={formData.managerId}
            onChange={(e) => setFormData({...formData, managerId: e.target.value})}
            required
          >
            <option value="">Select Project Manager</option>
            {projectManagers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
            ))}
          </select>
          {projectManagers.length === 0 && (
            <small className="form-helper">No project managers available</small>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" onClick={resetForm} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {editingProject ? 'Update' : 'Create'} Project
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      <div className="projects-grid">
        {filteredProjects.map(project => (
          <div key={project.id} className="project-card">
            <div className="project-header">
              <h3 className="project-title">{project.name}</h3>
              <span className={`status-badge ${getStatusColor(project.status)}`}>
                {project.status.replace('_', ' ')}
              </span>
            </div>

            <p className="project-description">{project.description}</p>

            <div className="project-progress">
              <div className="progress-label">
                <span className="empinfo">Progress</span>
                <span className="progress-percentage">{project.progress}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${project.progress}%` }}
                ></div>
              </div>
            </div>

            <div className="project-meta">
              <div className="project-manager">
                <i className="fas fa-user"></i>
                <span className="meta-text">{project.managerName}</span>
              </div>
              <div className="project-dates">
                <i className="fas fa-calendar"></i>
                <span className="meta-textt">{project.startDate} - {project.endDate}</span>
              </div>
              <div className="project-budget">
                <i className="fas fa-dollar-sign"></i>
                <span className="meta-textt">${project.budget?.toLocaleString()}</span>
              </div>
            </div>

            {canEditProject && (
              <div className="project-actions">
                <button 
                  onClick={() => handleEdit(project)}
                  className="btn btn-sm btn-outline"
                >
                  <i className="fas fa-edit"></i>
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}

        {filteredProjects.length === 0 && (
          <div className="no-data">
            <i className="fas fa-briefcase no-data-icon"></i>
            <h3 className="no-data-title">No Projects Found</h3>
            <p className="no-data-text">
              {currentUser?.role === 'project_manager' 
                ? 'No projects assigned to you yet.' 
                : 'Create your first project to get started.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsView;